import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Tabs, TabType } from '../../../Components/Tabs/Tabs';
import { SectionGroupId } from '../../../Helpers/questionnaireAnswers';
import { useAssistedApplicationContext } from '../../../Hooks/useAssistedApplicationContext';
import { NumberOfVisibleErrorsBySectionGroup } from '../AssistedApplicationContainer';
import { HeaderTabContainer } from './Styles';

export function HeaderTabBar({
  numberOfVisibleErrorsBySectionGroup,
  showCompletionIcons,
  onScrollToTop,
}: {
  showCompletionIcons: boolean;
  numberOfVisibleErrorsBySectionGroup: NumberOfVisibleErrorsBySectionGroup;
  onScrollToTop?: () => void;
}): ReactElement {
  const { currentSectionGroupId, onSwitchSectionGroupTab } = useAssistedApplicationContext();
  const { t } = useTranslation();
  const { sectionGroups, numberOfErrorsForInsuredsTab, insuredsTabIsCompleted } = useMemo(() => {
    const sectionGroups = new Map<string, { numberOfErrors: number; completed: boolean; label: string; id: string }>();
    let nInsuredsTabErrors = 0;
    let insuredsHasOneIncompleteProposedInsured = false;

    for (const visibleErrorsForSectionGroup of numberOfVisibleErrorsBySectionGroup) {
      const { sectionGroupId, numberOfErrors, completed } = visibleErrorsForSectionGroup;

      if (sectionGroupId.startsWith(SectionGroupId.insuredPeople)) {
        nInsuredsTabErrors += numberOfErrors;
        if (!insuredsHasOneIncompleteProposedInsured && !completed) {
          insuredsHasOneIncompleteProposedInsured = true;
        }
      } else {
        const existing = sectionGroups.get(sectionGroupId) || {
          numberOfErrors: 0,
          completed: true,
          label: sectionGroupId,
          id: sectionGroupId,
        };

        sectionGroups.set(sectionGroupId, {
          numberOfErrors: existing.numberOfErrors + numberOfErrors,
          completed: existing.completed && completed,
          label: existing.label,
          id: existing.id,
        });
      }
    }

    return {
      sectionGroups,
      numberOfErrorsForInsuredsTab: nInsuredsTabErrors,
      insuredsTabIsCompleted: !showCompletionIcons ? false : !insuredsHasOneIncompleteProposedInsured,
    };
  }, [numberOfVisibleErrorsBySectionGroup, showCompletionIcons]);

  const tabs: (TabType & { id: string })[] = useMemo(() => {
    const result: (TabType & { id: string })[] = [
      {
        label: t('assistedApplication.multiInsuredTabs.insuredPersons'),
        numberOfErrors: numberOfErrorsForInsuredsTab,
        completed: insuredsTabIsCompleted,
        id: SectionGroupId.insuredPeople,
      },
    ];

    sectionGroups.forEach(({ label, numberOfErrors, completed, id }) => {
      result.push({
        label: t(`assistedApplication.multiInsuredTabs.${label}`),
        numberOfErrors: showCompletionIcons ? numberOfErrors : 0,
        completed: !showCompletionIcons ? false : completed,
        id,
      });
    });

    return result;
  }, [sectionGroups, numberOfErrorsForInsuredsTab, insuredsTabIsCompleted, showCompletionIcons, t]);

  const onSwitchTabs = useCallback(
    (newValue: number) => {
      const sectionGroupId = tabs[newValue]?.id || SectionGroupId.insuredPeople;
      // Reuse parent-provided scroll-to-top behavior (scrollContainerRef in AssistedApplicationView)
      onScrollToTop?.();
      onSwitchSectionGroupTab(sectionGroupId as SectionGroupId);
    },
    [onScrollToTop, onSwitchSectionGroupTab, tabs],
  );

  const currentTabIndex = useMemo(() => {
    return tabs.findIndex(({ id }) => id === currentSectionGroupId) || 0;
  }, [currentSectionGroupId, tabs]);

  return (
    <HeaderTabContainer>
      <Tabs value={currentTabIndex} onChange={(_event, newValue) => onSwitchTabs(newValue)} tabs={tabs} />
    </HeaderTabContainer>
  );
}
